/**
 * Single entry point for everything a storefront configures the widget with. Instead of scattered
 * `window.*` globals (`window.shopId`, `window.accessToken`, `window.tradeInTheme`, …) the embed sets
 * one `window.tradeInConfig` object, and every reader goes through the accessors here — so there's
 * exactly one place that knows the shape.
 *
 * `window.Shopify` is deliberately NOT folded in: it's Shopify's own platform global (set by the
 * Shopify storefront, not by us), so we only READ it as a storefront-context fallback for
 * locale/currency/country. Non-Shopify storefronts supply the same context via `tradeInConfig.session`.
 */

/**
 * How the in-app flow routes. Embedded in a storefront, `Memory` keeps routing in memory so the
 * widget never touches (or 404s) the host page's URL. A standalone full-page deploy can opt into
 * `Hash` (`#/details`) or `Browser` (clean `/details`) to get working back/forward + deep links.
 *   Memory  — (default) in-memory; no URL, no browser back/forward. Safe for storefront embeds.
 *   Hash    — routes live in the URL hash; back/forward + deep links, no host server routing needed.
 *   Browser — clean history-API URLs; needs the page served as an SPA (use `routerBasename` off root).
 */
export enum RouterMode {
  Memory = 'memory',
  Hash = 'hash',
  Browser = 'browser',
}

/**
 * Which built-in artwork draws the "how it works" steps.
 *   Badge — (default) filled illustrations on a solid disc in the accent colour.
 *   Line  — fine-line drawings with no disc (a t-shirt on a hanger, a parcel with a bow, a pin with a
 *           heart), stroked in the accent colour, for storefronts where the badges read as heavy.
 *   Classic — v1's own step icons (a hand picking a tile, an open box, a box with a down arrow), in
 *           the accent colour, so a brand moving from v1 keeps the icons its customers know.
 */
export enum StepIconStyle {
  Badge = 'badge',
  Line = 'line',
  Classic = 'classic',
}

/** The consolidated config object a storefront sets on `window` before the bundle loads. */
export interface TradeInConfig {
  /** Shop the consumer is trading in with. */
  shopId?: string
  /** Brand-level fallback for shopId (any shop under the brand may log in). */
  brandId?: string
  /**
   * Let a consumer whose token belongs to a *different* shop than `shopId` sign in anyway — a brand
   * house serving several shops from one embed. Core then answers with the consumer's own shopId,
   * which the session adopts. Off (the default), such a token is rejected as an invalid-brand sign-in.
   */
  multiLogin?: boolean
  /** Login token, exchanged for the real session via ConsumerLogin. Often supplied via URL instead. */
  accessToken?: string
  /**
   * Colour theme: "blue" | "grey" | "squaregrey" | "ink" | "squareink" | "wine" | "squarewine" |
   * "stone" | "linen" | "squarelinen" | "teal" | "slate" | "rose" | "dark" | "spejdersport";
   * anything else falls through to the default palette. A "square…" theme is its palette with square
   * corners and uppercase tracked buttons, for storefronts built that way; "stone" is square-cornered
   * too but keeps sentence-case buttons and our default type scale, for the warm-neutral shops that
   * are not tracked-capital; "teal" has square cards, 6px sentence-case buttons and capital headings;
   * "slate" has square cards, 10px tracked-capital buttons and regular-weight olive headings.
   * Brand-named themes (spejdersport) also carry that brand's typeface stack.
   *
   * Space-separated words layer a brand's overrides onto a base theme: "squaregrey gailisva" is
   * squaregrey with GAI+LISVA's text colour, heading weight, button tracking and step artwork on
   * top. The base comes first; a brand layer ("gailisva") is never used on its own.
   */
  theme?: string
  /**
   * CSS font-family values for body text and headings. Either can be set independently. `heading`
   * also styles money/value figures and redemption codes, so choose a face with suitable numerals
   * (including tabular figures where requested by the UI). The host page must load custom webfonts;
   * these settings only select available font families.
   */
  fonts?: {
    body?: string
    heading?: string
  }
  /**
   * Sticky-topbar top offset so it clears the host's own sticky header. Number = px, or a CSS length.
   * Set it to the height of that header (givnberlin.com 55, wheat.de 71). The desktop summary rail
   * rides the same offset. When the header's height changes per breakpoint, reference the host
   * theme's own live-measured vars instead of one px value — Prestige (klitmollercollective.dk,
   * 163px desktop / 91.5px mobile): "calc(var(--announcement-bar-height, 0px) + var(--header-height, 0px))".
   *
   * Only the offset is configured here — whether sticky *works* is handled for you: a storefront
   * wrapper at `overflow: hidden` would otherwise capture the topbar's sticky positioning, and
   * `util/stickyAncestors` repairs that on connect.
   */
  stickyOffset?: number | string
  /**
   * The login front page's full-bleed banner behind the login card. All optional — unset falls back
   * to the theme's default gradient. The layers, back to front: `gradient` → `color` → theme default
   * underneath, with `image` painted over it, and `video` over that. Several images cross-fade into
   * one another on a timer, so the layer beneath is what shows while they load, and what remains if
   * they all fail.
   */
  banner?: {
    /** One hero image URL, or several to cross-fade between. */
    image?: string | string[]
    /**
     * A silent, looping MP4 played over the images. The images stay its fallback: they show while it
     * buffers, when the browser won't autoplay it, and for visitors who ask for reduced motion or
     * data saving — so a brand setting `video` should always set `image` too.
     */
    video?: string
    /**
     * Which part of the image (and video) stays in view when the banner's shape crops it — a CSS
     * `background-position` such as "center top" or "25% top"; default "center". The images fill
     * the banner edge to edge, so a wide screen crops their top and bottom: a photo whose subject's
     * face sits high needs "top" or it is cut off.
     */
    position?: string
    /**
     * The banner's minimum height — px, or any CSS length; default 520px on desktop, the login card's
     * own height on a phone. The banner is as wide as the embed, so on a desktop a 16:9 video or
     * photo loses its top and bottom: "56.25cqw" (the embed width × 9/16) shows the whole frame at
     * every width, and e.g. "min(56.25cqw, 800px)" caps it on very wide screens.
     */
    minHeight?: number | string
    /** Seconds each image holds before cross-fading to the next; default 30. Ignored for one image. */
    rotateSeconds?: number
    /** A CSS gradient (or any `background-image` value) — the layer beneath `image`. */
    gradient?: string
    /** A solid background colour — the layer beneath `image` when there's no `gradient`. */
    color?: string
    /** Dark overlay opacity over the banner (0–1) for text legibility; default ~0.42. */
    scrim?: number
  }
  /**
   * Icons for the "how it works" steps (login page and intro dialog). `style` picks the built-in
   * artwork ("badge" by default, "line", or v1's "classic"). `images` are up to 3 URLs that replace
   * the built-in icons by index (a missing/empty entry keeps that step's built-in one). `width`
   * sizes those custom images (px or CSS length).
   */
  stepIcons?: {
    style?: StepIconStyle
    images?: string[]
    width?: number | string
  }
  /**
   * A hero video shown full-width below the "how it works" steps (autoplay, muted, looping, with a
   * mute toggle — mirrors v1's brand video). Unset → no video. An MP4 URL.
   */
  video?: string
  /**
   * The illustration shown on the "Search catalogue" tab that hints where to find the SKU / style
   * number (a garment care label by default). A brand can point this at its own image — e.g. a
   * jewelry hallmark or product tag. Unset → the bundled care-label illustration.
   */
  searchLabelImage?: string
  /**
   * V2-only product-search override. An explicit boolean wins over Core's shared V1/V2 setting;
   * unset keeps Core authoritative. Useful for previewing V2 search without changing the live V1 app.
   */
  productSearchEnabled?: boolean
  /** Non-Shopify storefront context (language/country/currency) — same role `window.Shopify` plays. */
  session?: {
    language?: string
    countryCode?: string
    currency?: string
  }
  /**
   * Per-language overrides for the "subject" word lexicon — the tokens our copy interpolates so a
   * brand can name what it trades in and what it pays out. Two groups:
   *   {noun} / {nounPoss} / {nounDef}                  — "jewelry"/"shoes" instead of "clothing"
   *   {reward} / {rewardPoss} / {rewardDef} / {rewardPlural} — "gift card" instead of "voucher"
   * Shape: `{ da: { noun: 'grej', nounPoss: 'dit grej', reward: 'gavekort', … }, en: {…} }`; a partial
   * override keeps the dictionary's value for every form it omits. Because a brand supplies the FULL
   * determiner+noun phrase per form, Danish/German article agreement (dit/din/dine, det/den/de) is
   * handled by the brand's own wording — we don't compute it. The German copy still names the reward
   * literally (its articles are case-marked), so only da/en respond to the reward tokens today; see
   * the note in dictionaries/de.ts. Loosely typed (locale → token → phrase) to keep this module free
   * of a localization import cycle.
   */
  terms?: Record<string, Record<string, string>>
  /**
   * Escape hatch: per-language deep overrides of ANY localization string, for wording the token
   * lexicon can't express (full sentence rewrites, bespoke legal text). Shape mirrors the dictionary,
   * partially: `{ da: { terms: { introLead: '…' }, login: { title: '…' } } }`. Deep-merged over the
   * bundled dictionary in loadDictionary. Loosely typed (locale → DeepPartial<Dict>).
   */
  dictionary?: Record<string, unknown>
  /**
   * Which router the in-app flow uses. Defaults to `Memory` (isolated, no URL) — right for a
   * storefront embed. Set `Browser` or `Hash` on a standalone full-page deploy for real navigation.
   */
  router?: RouterMode
  /**
   * Base path for `Browser` routing when the app isn't served at the site root (e.g. `/trade-in`).
   * Ignored for `Memory`/`Hash`. Unset → root.
   */
  routerBasename?: string
}

const cfg = (): TradeInConfig => window.tradeInConfig ?? {}

// --- our settings ---

export const getShopId = (): string => cfg().shopId ?? ''
export const getBrandId = (): string => cfg().brandId ?? ''
export const getAccessToken = (): string => cfg().accessToken ?? ''
export const getMultiLogin = (): boolean => cfg().multiLogin === true
export const getTheme = (): string | null => cfg().theme ?? null
export const getFonts = (): NonNullable<TradeInConfig['fonts']> | null => cfg().fonts ?? null
export const getStickyOffset = (): number | string | null => cfg().stickyOffset ?? null
export const getBanner = (): NonNullable<TradeInConfig['banner']> | null => cfg().banner ?? null
export const getStepIcons = (): NonNullable<TradeInConfig['stepIcons']> | null => cfg().stepIcons ?? null
export const getVideo = (): string | null => cfg().video ?? null
export const getSearchLabelImage = (): string | null => cfg().searchLabelImage ?? null

/** The built-in step artwork to draw. Unset or unrecognised values keep the default badges. */
export function getStepIconStyle(): StepIconStyle {
  const style = cfg().stepIcons?.style
  return style && Object.values(StepIconStyle).includes(style) ? style : StepIconStyle.Badge
}

/** Resolves V2 product search, preferring an explicit embed override and otherwise using Core. */
export function resolveProductSearchEnabled(coreEnabled?: boolean | null): boolean {
  return cfg().productSearchEnabled ?? coreEnabled ?? false
}

/** Seconds a banner image holds before the cross-fade. Nonsense/absent values fall back to half a minute. */
export const getBannerRotateSeconds = (): number => {
  const configured = cfg().banner?.rotateSeconds
  return typeof configured === 'number' && Number.isFinite(configured) && configured >= 5
    ? configured
    : 30
}

/**
 * The configured banner images to cross-fade through, in display order. Empty entries are dropped and
 * the legacy single-string form yields one image. The UTC day index picks which image *leads*, so the
 * first thing a visitor sees still varies from day to day; the rest follow in configured order.
 */
export function orderBannerImages(
  configured: NonNullable<TradeInConfig['banner']>['image'],
  date = new Date(),
): string[] {
  const values = (Array.isArray(configured) ? configured : [configured])
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => value.trim())

  if (values.length <= 1) return values
  // A pre-epoch clock yields a negative index, which `slice` reads from the end — still a rotation.
  const lead = Math.floor(date.getTime() / 86_400_000) % values.length
  return [...values.slice(lead), ...values.slice(0, lead)]
}

/** The image following `current` in a wrap-around cycle; an unknown/absent `current` starts at the top. */
export function nextBannerImage(images: string[], current: string | undefined): string | undefined {
  if (images.length === 0) return undefined
  return images[(images.indexOf(current ?? '') + 1) % images.length]
}

/**
 * The banner video to play, or null to stay on the images: when none is configured, when the visitor
 * asks for reduced motion (no auto-playing content), or when they're saving data (a few MB spared).
 */
export function resolveBannerVideo(
  configured: NonNullable<TradeInConfig['banner']>['video'],
  viewer: { reducedMotion: boolean; saveData: boolean },
): string | null {
  const url = typeof configured === 'string' ? configured.trim() : ''
  if (!url || viewer.reducedMotion || viewer.saveData) return null
  return url
}

/** Router mode for the flow; anything not a known value falls back to the isolated `Memory` default. */
export const getRouterMode = (): RouterMode =>
  Object.values(RouterMode).includes(cfg().router as RouterMode)
    ? (cfg().router as RouterMode)
    : RouterMode.Memory

/** Base path for `Browser` routing when the app isn't at the site root; null → root. */
export const getRouterBasename = (): string | null => cfg().routerBasename ?? null

/**
 * The basename `BrowserRouter` should actually use on this page. React Router matches the basename
 * against the path literally, so a mismatch renders nothing (blank widget). Two storefront realities
 * are absorbed here:
 *   - a trailing slash in config ("/apps/retrade/") is dropped, so the page works with or without one;
 *   - Shopify Markets serves the same app proxy under a locale prefix ("/da/apps/retrade",
 *     "/en-de/apps/retrade"), so whatever precedes the configured basename (on a segment boundary)
 *     is kept — navigation then stays inside the shopper's chosen market/language.
 * If the configured basename isn't in the path at all, it is returned as-is (the old behaviour).
 */
export function resolveRouterBasename(configured: string | null, pathname: string): string | undefined {
  const base = configured?.replace(/\/+$/, '')
  if (!base) return undefined
  const at = pathname.indexOf(base)
  const next = pathname.charAt(at + base.length)
  const onSegmentBoundary = next === '' || next === '/'
  return at >= 0 && onSegmentBoundary ? pathname.slice(0, at + base.length) : base
}

/** A Shopify Markets locale subfolder at the start of the path: `/da/…`, `/en-de/…`, `/pt-br/…`. */
const LOCALE_PREFIX_RE = /^\/([a-z]{2})(?:-[a-z]{2,4})?(?=\/|$)/i

/** The language of a Shopify Markets locale prefix in the path (`/en-de/apps/retrade` → `en`), if any. */
export function languageFromPath(pathname: string): string | undefined {
  return LOCALE_PREFIX_RE.exec(pathname)?.[1].toLowerCase()
}

// --- storefront context (config.session, then Shopify's own platform global) ---

/**
 * The storefront's language: the embed config's `session.language`, then Shopify's platform global,
 * then a Markets locale prefix in the page URL (`/da/apps/retrade`) when neither is available.
 */
export const getStoreLanguage = (): string | undefined =>
  cfg().session?.language ?? window.Shopify?.locale ?? languageFromPath(window.location?.pathname ?? '')

export const getStoreCountry = (): string | undefined =>
  cfg().session?.countryCode ?? window.Shopify?.country

export const getStoreCurrency = (): string | undefined =>
  cfg().session?.currency ?? window.Shopify?.currency?.active

/** Brand overrides for the subject-word lexicon in a given locale (merged over the dict defaults). */
export const getTermsOverride = (locale: string): Record<string, string> | undefined =>
  cfg().terms?.[locale]

/** Brand deep-override of dictionary strings for a given locale (partial, merged over the bundled dict). */
export const getDictionaryOverride = (locale: string): Record<string, unknown> | undefined =>
  cfg().dictionary?.[locale] as Record<string, unknown> | undefined

/**
 * Clear the embed-supplied access token on logout so a re-read of bootstrap doesn't re-authenticate
 * off the original token.
 */
export function clearEmbedAccessToken(): void {
  if (window.tradeInConfig) window.tradeInConfig.accessToken = undefined
}
